let installedTagId: string | null = null;

type GoogleTagPrimitive = string | number | boolean;

type GoogleTagEventParams = Record<string, GoogleTagPrimitive | undefined>;

type GoogleTagEventCallback = () => void;

type GoogleAdsEnhancedConversionInput = {
  email?: string | null;
  phoneNumber?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
};

const normalizeEmail = (value?: string | null): string | undefined => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  return normalized || undefined;
};

const normalizePhoneNumber = (value?: string | null): string | undefined => {
  const digitsOnly = String(value || '').replace(/\D+/g, '');
  if (digitsOnly.length < 8) return undefined;

  const withoutInternationalPrefix = digitsOnly.startsWith('00') ? digitsOnly.slice(2) : digitsOnly;
  return `+${withoutInternationalPrefix}`;
};

const splitName = (fullName?: string | null) => {
  const parts = String(fullName || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    return { firstName: undefined, lastName: undefined };
  }

  return {
    firstName: parts[0],
    lastName: parts.length > 1 ? parts.slice(1).join(' ') : undefined,
  };
};

const sanitizeParams = (params: GoogleTagEventParams = {}): Record<string, GoogleTagPrimitive> => {
  const normalized: Record<string, GoogleTagPrimitive> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      normalized[key] = value;
    }
  }
  return normalized;
};

export const installGoogleTag = (tagId?: string | null) => {
  if (typeof window === 'undefined') return;
  const normalizedTagId = (tagId || '').trim();
  if (!normalizedTagId) return;
  if (installedTagId === normalizedTagId) return;

  const scriptSrc = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(normalizedTagId)}`;
  const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);

  // If the static HTML already loaded this exact tag, just register and skip re-init
  // (avoids double page_view event)
  if (existingScript && typeof (window as any).gtag === 'function') {
    installedTagId = normalizedTagId;
    return;
  }

  if (!existingScript) {
    const script = document.createElement('script');
    script.async = true;
    script.src = scriptSrc;
    document.head.appendChild(script);
  }

  (window as any).dataLayer = (window as any).dataLayer || [];
  const gtag = (...args: unknown[]) => {
    (window as any).dataLayer.push(args);
  };
  (window as any).gtag = (window as any).gtag || gtag;
  (window as any).gtag('js', new Date());
  (window as any).gtag('config', normalizedTagId);
  installedTagId = normalizedTagId;
};

export const trackGoogleTagEvent = (eventName: string, params: GoogleTagEventParams = {}) => {
  if (typeof window === 'undefined') return;
  const gtag = (window as any).gtag;
  if (typeof gtag !== 'function') return;
  gtag('event', eventName, sanitizeParams(params));
};

export const trackGoogleTagEventWithCallback = (
  eventName: string,
  params: GoogleTagEventParams = {},
  onComplete?: GoogleTagEventCallback,
  timeoutMs = 1200
) => {
  if (typeof window === 'undefined') {
    onComplete?.();
    return;
  }

  const gtag = (window as any).gtag;
  if (typeof gtag !== 'function') {
    onComplete?.();
    return;
  }

  let completed = false;
  const finish = () => {
    if (completed) return;
    completed = true;
    onComplete?.();
  };

  window.setTimeout(finish, timeoutMs);

  gtag('event', eventName, {
    ...sanitizeParams(params),
    event_callback: finish,
    event_timeout: timeoutMs,
  });
};

/**
 * Installs an additional gtag (e.g. Google Ads AW-xxxxx) alongside the
 * already-installed GA4 tag. Safe to call multiple times — deduplicates
 * by tag ID. The global `gtag` function is shared across tags.
 */
export const installExtraGoogleTag = (tagId?: string | null) => {
  if (typeof window === 'undefined') return;
  const normalizedTagId = (tagId || '').trim();
  if (!normalizedTagId) return;

  const scriptSrc = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(normalizedTagId)}`;
  if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = scriptSrc;
    document.head.appendChild(script);
  }

  (window as any).dataLayer = (window as any).dataLayer || [];
  if (typeof (window as any).gtag !== 'function') {
    (window as any).gtag = (...args: unknown[]) => {
      (window as any).dataLayer.push(args);
    };
  }
  (window as any).gtag('config', normalizedTagId);
};

/**
 * Fires a Google Ads conversion event.
 * @param adsId          - The AW-xxxxxxx account ID stored in build config
 * @param label          - Optional conversion label (AW-xxxxx/LABEL). If omitted fires at account level.
 * @param transactionId  - Optional transaction id for conversion dedup/reporting.
 * @param onComplete     - Optional callback fired by gtag when event is sent.
 */
export const trackGoogleAdsConversion = (
  adsId?: string | null,
  label?: string | null,
  transactionId?: string | number,
  onComplete?: () => void
) => {
  if (typeof window === 'undefined') return;
  const gtag = (window as any).gtag;
  if (typeof gtag !== 'function') return;
  const normalizedId = (adsId || '').trim();
  if (!normalizedId) return;

  const sendTo = label ? `${normalizedId}/${label.trim()}` : normalizedId;
  gtag('event', 'conversion', {
    send_to: sendTo,
    transaction_id: transactionId ? String(transactionId) : '',
    ...(onComplete ? { event_callback: onComplete } : {}),
  });
};

/**
 * Sends manual in-page user_data for Google Ads enhanced conversions.
 * Call this right before the conversion event.
 */
export const setGoogleAdsUserData = (input: GoogleAdsEnhancedConversionInput = {}) => {
  if (typeof window === 'undefined') return;
  const gtag = (window as any).gtag;
  if (typeof gtag !== 'function') return;

  const fallbackName = splitName(input.fullName);
  const firstName = String(input.firstName || fallbackName.firstName || '').trim() || undefined;
  const lastName = String(input.lastName || fallbackName.lastName || '').trim() || undefined;

  const userData: Record<string, unknown> = {};
  const email = normalizeEmail(input.email);
  const phoneNumber = normalizePhoneNumber(input.phoneNumber);

  if (email) userData.email = email;
  if (phoneNumber) userData.phone_number = phoneNumber;

  if (firstName || lastName) {
    userData.address = {
      ...(firstName ? { first_name: firstName } : {}),
      ...(lastName ? { last_name: lastName } : {}),
    };
  }

  if (Object.keys(userData).length === 0) return;
  gtag('set', 'user_data', userData);
};
